/**
 * TanStack Query hooks for project data.
 *
 * Provides queries and mutations for project CRUD, membership, and tag assignment.
 * Uses the authenticated API client from use-api.ts.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { useApiClient } from "./use-api.ts"

// ─── Query Keys ─────────────────────────────────────────────

export const projectKeys = {
  all: () => ["projects"] as const,
  detail: (projectId: string) => ["projects", projectId] as const,
  members: (projectId: string) => ["projects", projectId, "members"] as const,
  memberCandidates: (projectId: string) => ["projects", projectId, "member-candidates"] as const
}

// ─── Types ──────────────────────────────────────────────────

export interface ProjectFilters {
  status?: "active" | "archived"
  tag_ids?: string
  page?: number
  limit?: number
}

// ─── Project Queries ────────────────────────────────────────

/** Fetch project list with optional filters */
export function useProjects(filters?: ProjectFilters) {
  const api = useApiClient()
  const params = new URLSearchParams()
  if (filters?.status) params.set("status", filters.status)
  if (filters?.tag_ids) params.set("tag_ids", filters.tag_ids)
  if (filters?.page) params.set("page", String(filters.page))
  if (filters?.limit) params.set("limit", String(filters.limit))

  const queryString = params.toString()
  const path = `/projects${queryString ? `?${queryString}` : ""}`

  return useQuery({
    queryKey: [...projectKeys.all(), filters ?? {}],
    queryFn: () => api(path),
    enabled: true
  })
}

/** Fetch single project */
export function useProject(projectId: string) {
  const api = useApiClient()
  return useQuery({
    queryKey: projectKeys.detail(projectId),
    queryFn: () => api(`/projects/${projectId}`),
    enabled: Boolean(projectId)
  })
}

/** Fetch project members */
export function useProjectMembers(projectId: string) {
  const api = useApiClient()
  return useQuery({
    queryKey: projectKeys.members(projectId),
    queryFn: () => api(`/projects/${projectId}/members`),
    enabled: Boolean(projectId)
  })
}

/** Fetch assignable member candidates for a project */
export function useProjectMemberCandidates(projectId: string) {
  const api = useApiClient()
  return useQuery({
    queryKey: projectKeys.memberCandidates(projectId),
    queryFn: () => api(`/projects/${projectId}/member-candidates?limit=100`),
    enabled: Boolean(projectId)
  })
}

// ─── Project Mutations ──────────────────────────────────────

/** Create a new project */
export function useCreateProject() {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: {
      name: string
      description?: string | null
      mode?: "standard" | "queued"
      workflow?: "radiology" | "pathology"
      status?: "draft" | "active" | "paused" | "completed" | "archived"
      review_enabled?: boolean
      readers_per_case?: number
      sort_metric_key?: string | null
      sort_metric_type?: "integer" | "float" | null
      sort_metric_direction?: "asc" | "desc" | null
      due_date?: string | null
    }) =>
      api(`/projects`, {
        method: "POST",
        body: JSON.stringify(data)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all() })
    }
  })
}

/** Update an existing project */
export function useUpdateProject(projectId: string) {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: {
      name?: string
      description?: string | null
      mode?: "standard" | "queued"
      workflow?: "radiology" | "pathology"
      status?: "draft" | "active" | "paused" | "completed" | "archived"
      review_enabled?: boolean
      readers_per_case?: number
      sort_metric_key?: string | null
      sort_metric_type?: "integer" | "float" | null
      sort_metric_direction?: "asc" | "desc" | null
      due_date?: string | null
    }) =>
      api(`/projects/${projectId}`, {
        method: "PATCH",
        body: JSON.stringify(data)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) })
      queryClient.invalidateQueries({ queryKey: projectKeys.all() })
    }
  })
}

/** Archive a project */
export function useArchiveProject(projectId: string) {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      api(`/projects/${projectId}/archive`, {
        method: "PATCH"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) })
      queryClient.invalidateQueries({ queryKey: projectKeys.all() })
    }
  })
}

/** Unarchive (restore) a project */
export function useUnarchiveProject(projectId: string) {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      api(`/projects/${projectId}/unarchive`, {
        method: "PATCH"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) })
      queryClient.invalidateQueries({ queryKey: projectKeys.all() })
    }
  })
}

/** Permanently delete a project */
export function useDeleteProject(projectId: string) {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      api(`/projects/${projectId}`, {
        method: "DELETE"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all() })
    }
  })
}

// ─── Member Mutations ───────────────────────────────────────

/** Add a project member */
export function useAddProjectMember(projectId: string) {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { user_id: string; role: "project_admin" | "annotator" | "reviewer" | "viewer" }) =>
      api(`/projects/${projectId}/members`, {
        method: "POST",
        body: JSON.stringify(data)
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) })
      queryClient.invalidateQueries({
        queryKey: projectKeys.memberCandidates(projectId)
      })
    }
  })
}

/** Update project member role */
export function useUpdateProjectMemberRole(projectId: string) {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { memberId: string; role: "project_admin" | "annotator" | "reviewer" | "viewer" }) =>
      api(`/projects/${projectId}/members/${data.memberId}`, {
        method: "PATCH",
        body: JSON.stringify({ role: data.role })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) })
      queryClient.invalidateQueries({
        queryKey: projectKeys.memberCandidates(projectId)
      })
    }
  })
}

/** Remove project member */
export function useRemoveProjectMember(projectId: string) {
  const api = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (memberId: string) =>
      api(`/projects/${projectId}/members/${memberId}`, {
        method: "DELETE"
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) })
    }
  })
}
